import React, { useState } from "react";
import { Camera, Mic, RefreshCcw, X as XIcon, ChevronDown, AlertTriangle } from "lucide-react";

/**
 * CameraPermissionGate — the overlay that runs before/around a live camera element.
 *
 * Props:
 *   state: one of 'idle' | 'requesting' | 'ready' | 'denied' | 'error'
 *   error: string message when state is 'error' or 'denied'
 *   onAcquire: () => void — user-gesture callback to request camera/mic
 *   devices: { videoIn:[], audioIn:[] }
 *   current:  { videoDeviceId, audioDeviceId, facingMode }
 *   onSelect: ({ videoDeviceId, audioDeviceId }) => void
 *   onFlip: () => void
 */
export default function CameraPermissionGate({ state, error, onAcquire, devices, current, onSelect, onFlip, primaryLabel = "Enable camera & mic" }) {
  const [picker, setPicker] = useState(false);
  const hasMultipleCameras = devices?.videoIn?.length >= 2;

  if (state === "ready") {
    // Shown as a compact toolbar on top of the live preview
    return (
      <div className="absolute top-3 right-16 flex items-center gap-2" data-testid="camera-toolbar">
        {hasMultipleCameras && (
          <button
            onClick={onFlip}
            data-testid="camera-flip-btn"
            className="w-9 h-9 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center"
            title="Flip camera"
          >
            <RefreshCcw className="w-4 h-4" />
          </button>
        )}
        <div className="relative">
          <button
            onClick={() => setPicker((p) => !p)}
            data-testid="camera-picker-toggle"
            className="px-2.5 py-1.5 rounded-full bg-black/70 hover:bg-black/90 text-white text-xs flex items-center gap-1"
          >
            <Camera className="w-3 h-3" /> Devices <ChevronDown className="w-3 h-3" />
          </button>
          {picker && (
            <div className="absolute right-0 top-full mt-2 w-64 bg-[#0B1120] border border-white/15 rounded-xl shadow-2xl z-20 overflow-hidden" data-testid="camera-picker-menu">
              <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-tv-accent font-bold border-b border-white/10 flex items-center gap-1">
                <Camera className="w-3 h-3" /> Camera
              </div>
              {(devices.videoIn || []).map((d) => (
                <button
                  key={d.deviceId}
                  onClick={() => { onSelect({ videoDeviceId: d.deviceId }); setPicker(false); }}
                  data-testid={`camera-device-${d.deviceId.slice(0, 10)}`}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 ${current?.videoDeviceId === d.deviceId ? "text-tv-accent font-semibold" : "text-white/85"}`}
                >
                  {d.label || `Camera ${d.deviceId.slice(0, 5)}`}
                </button>
              ))}
              <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-tv-accent font-bold border-y border-white/10 flex items-center gap-1">
                <Mic className="w-3 h-3" /> Microphone
              </div>
              {(devices.audioIn || []).map((d) => (
                <button
                  key={d.deviceId}
                  onClick={() => { onSelect({ audioDeviceId: d.deviceId }); setPicker(false); }}
                  data-testid={`mic-device-${d.deviceId.slice(0, 10)}`}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 ${current?.audioDeviceId === d.deviceId ? "text-tv-accent font-semibold" : "text-white/85"}`}
                >
                  {d.label || `Mic ${d.deviceId.slice(0, 5)}`}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Full-screen overlay when not ready
  const requesting = state === "requesting";
  const denied = state === "denied";
  const errored = state === "error";

  return (
    <div data-testid="camera-permission-gate" className="absolute inset-0 z-10 flex items-center justify-center bg-black/85 backdrop-blur-sm p-6 text-center">
      <div className="max-w-md">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto ${denied || errored ? "bg-red-500/20" : "bg-tv-accent/20"}`}>
          {denied || errored ? <AlertTriangle className="w-7 h-7 text-red-300" /> : <Camera className="w-7 h-7 text-tv-accent" />}
        </div>
        <h3 className="font-display font-black text-2xl sm:text-3xl text-white mt-4 tracking-tight">
          {denied ? "Camera blocked" : errored ? "Camera unavailable" : "Camera & mic needed"}
        </h3>
        <p className="text-white/70 text-sm mt-2 leading-relaxed">
          {denied
            ? "Tap In TV needs camera + microphone access to go live. Click the 🔒 icon next to the URL, allow both, then reload."
            : errored
            ? error || "Something went wrong requesting your camera."
            : "We'll ask your browser for permission to use your camera and microphone. Tap below when you're ready."}
        </p>
        {(requesting || state === "idle") && (
          <button
            onClick={onAcquire}
            disabled={requesting}
            data-testid="camera-acquire-btn"
            className="mt-6 bg-tv-accent hover:bg-yellow-400 disabled:opacity-60 text-black font-bold px-6 py-3 rounded-full inline-flex items-center gap-2"
          >
            <Camera className="w-4 h-4" />
            {requesting ? "Requesting…" : primaryLabel}
          </button>
        )}
        {(denied || errored) && (
          <button
            onClick={onAcquire}
            data-testid="camera-retry-btn"
            className="mt-6 bg-white/10 hover:bg-white/20 text-white font-bold px-6 py-3 rounded-full inline-flex items-center gap-2"
          >
            <RefreshCcw className="w-4 h-4" /> Try again
          </button>
        )}
        <div className="text-[11px] text-white/40 mt-4">
          iPhone tip: if nothing happens, check Settings → Safari → Camera / Microphone → Allow.
        </div>
      </div>
    </div>
  );
}
